import * as React from "react";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  apiClient,
  ApiError,
  type Enrolment,
  type EnrolmentMove,
  type EnrolmentMovePlan,
} from "@alight/contracts";
import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
} from "@alight/ui";
import { AlertTriangle, Clock, MailCheck, MoveRight } from "lucide-react";

/**
 * MOVE ONE REGISTRATION TO ANOTHER EMAIL ADDRESS.
 *
 * The correction dialog next door edits a family's address by re-keying
 * everything they own — which is refused outright when the destination is
 * already somebody here. That refusal is the common case, not the rare one:
 * the address on a registration is usually wrong precisely because the right
 * one already belongs to the family.
 *
 * This screen does the narrow thing instead. It shows the host EXACTLY what
 * would move before anything is asked for — which registrations, whether an
 * invoice follows, who is already at the destination and what the old address
 * keeps — and then sends the destination a confirmation link. Nothing moves
 * until that link is clicked, which is why the button says "email" and not
 * "move": an admin asserting that a child belongs to an address is not, by
 * itself, evidence that they do.
 */

export function MoveRegistrationDialog({
  api,
  enrolment,
  onClose,
}: {
  api: ReturnType<typeof apiClient>;
  enrolment: Enrolment | null;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const open = enrolment !== null;

  const [toEmail, setToEmail] = React.useState("");
  const [toName, setToName] = React.useState("");
  const [toPhone, setToPhone] = React.useState("");
  const [notifyOld, setNotifyOld] = React.useState(true);
  const [sent, setSent] = React.useState<{ to: string; emailed: boolean } | null>(null);

  /* Debounced so the preview follows the host's typing without a request per
     keystroke — it is a read, but it is several queries deep. */
  const [debounced, setDebounced] = React.useState("");
  React.useEffect(() => {
    const t = setTimeout(() => setDebounced(toEmail.trim().toLowerCase()), 350);
    return () => clearTimeout(t);
  }, [toEmail]);

  const existing = useQuery({
    queryKey: ["admin", "enrolment-moves", enrolment?.id ?? 0],
    queryFn: () => api.adminListEnrolmentMoves({ enrolment_id: enrolment!.id }),
    enabled: open,
  });
  const pending = (existing.data ?? []).find((m) => m.state === "awaiting") ?? null;

  const plan = useQuery({
    queryKey: ["admin", "enrolment-move-plan", enrolment?.id ?? 0, debounced],
    queryFn: () => api.adminEnrolmentMovePlan(enrolment!.id, debounced),
    enabled: open && debounced.length > 3,
    retry: false,
  });

  function refresh() {
    qc.invalidateQueries({ queryKey: ["admin", "enrolment-moves"] });
    qc.invalidateQueries({ queryKey: ["admin", "enrolments"] });
    qc.invalidateQueries({ queryKey: ["admin", "children"] });
  }

  const request = useMutation({
    mutationFn: () =>
      api.adminRequestEnrolmentMove(enrolment!.id, {
        to_email: debounced || toEmail.trim().toLowerCase(),
        ...(toName.trim() ? { to_name: toName.trim() } : {}),
        ...(toPhone.trim() ? { to_phone: toPhone.trim() } : {}),
        notify_old: notifyOld,
      }),
    onSuccess: (result) => {
      setSent({ to: result.move.to_email, emailed: result.emailed });
      refresh();
    },
  });

  const cancel = useMutation({
    mutationFn: (id: number) => api.adminCancelEnrolmentMove(id),
    onSuccess: refresh,
  });
  const resend = useMutation({
    mutationFn: (id: number) => api.adminResendEnrolmentMove(id),
    onSuccess: (result) => {
      setSent({ to: result.move.to_email, emailed: result.emailed });
      refresh();
    },
  });

  const blocked = plan.data?.blocked ?? null;
  const canSend = debounced.length > 3 && !blocked && plan.isSuccess && !request.isPending;

  return (
    <Dialog open={open} onOpenChange={(o) => (!o ? onClose() : undefined)}>
      <DialogContent className="max-h-[85vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Move {enrolment?.child_name}&apos;s registration</DialogTitle>
          <DialogDescription>
            Put this child under a different email address, without disturbing anyone else
            currently listed under {enrolment?.parent_email}. The new address is emailed a link
            asking them to confirm — the child does not move until they do.
          </DialogDescription>
        </DialogHeader>

        {sent ? (
          <SentPanel to={sent.to} emailed={sent.emailed} onDone={onClose} />
        ) : (
          <div className="space-y-5">
            {pending ? <PendingPanel move={pending} onCancel={() => cancel.mutate(pending.id)} onResend={() => resend.mutate(pending.id)} busy={cancel.isPending || resend.isPending} /> : null}

            <form
              onSubmit={(e) => {
                e.preventDefault();
                if (canSend) request.mutate();
              }}
              className="space-y-5"
            >
              <fieldset className="space-y-3">
                <legend className="text-xs font-semibold uppercase tracking-wide text-muted">
                  Move to
                </legend>
                <div className="space-y-1.5">
                  <Label htmlFor="move-email">The email address it should be under</Label>
                  <Input
                    id="move-email"
                    type="email"
                    autoComplete="off"
                    value={toEmail}
                    onChange={(e) => setToEmail(e.target.value)}
                    placeholder="parent@example.com"
                    required
                  />
                  <p className="text-xs text-muted">
                    Currently under <span className="font-medium text-ink">{enrolment?.parent_email}</span>.
                  </p>
                </div>
                <div className="grid gap-3 sm:grid-cols-2">
                  <div className="space-y-1.5">
                    <Label htmlFor="move-name">Their name (optional)</Label>
                    <Input
                      id="move-name"
                      value={toName}
                      onChange={(e) => setToName(e.target.value)}
                      placeholder={plan.data?.destination_name ?? "Leave blank to keep what's on file"}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="move-phone">Their phone (optional)</Label>
                    <Input
                      id="move-phone"
                      type="tel"
                      value={toPhone}
                      onChange={(e) => setToPhone(e.target.value)}
                    />
                  </div>
                </div>
              </fieldset>

              {blocked ? (
                <p role="alert" className="rounded-card border border-border bg-paper px-3 py-2 text-sm text-ink">
                  {blocked}
                </p>
              ) : plan.data ? (
                <PlanPanel plan={plan.data} />
              ) : plan.isFetching ? (
                <p className="text-sm text-muted">Working out what would move…</p>
              ) : null}

              <label className="flex items-start gap-2 text-sm text-ink" htmlFor="move-notify">
                <input
                  id="move-notify"
                  type="checkbox"
                  className="mt-0.5"
                  checked={notifyOld}
                  onChange={(e) => setNotifyOld(e.target.checked)}
                />
                <span>
                  Let {enrolment?.parent_email} know once it moves
                  <span className="block text-xs text-muted">
                    A short notice naming the child. The new address is only partly shown, in case
                    this one belongs to someone unrelated to the family.
                  </span>
                </span>
              </label>

              <div
                role="note"
                className="flex items-start gap-2 rounded-card border border-border bg-paper px-3 py-2 text-xs text-muted"
              >
                <MailCheck className="mt-0.5 h-4 w-4 flex-shrink-0" aria-hidden />
                <p>
                  Nothing moves yet. We email the new address a link, and the records move when
                  they confirm the child is theirs. The link lasts 7 days, and you can cancel or
                  re-send it here at any time.
                </p>
              </div>

              {request.error ? (
                <p className="text-sm text-accent-fg" role="alert">
                  {request.error instanceof ApiError
                    ? request.error.message
                    : "Couldn’t send that — please retry."}
                </p>
              ) : null}

              <DialogFooter>
                <Button type="button" variant="outline" onClick={onClose} disabled={request.isPending}>
                  Cancel
                </Button>
                <Button type="submit" disabled={!canSend}>
                  {request.isPending ? "Sending…" : "Email the new address to confirm"}
                </Button>
              </DialogFooter>
            </form>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

/** What the move would actually touch — the whole point of the screen. */
function PlanPanel({ plan }: { plan: EnrolmentMovePlan }) {
  return (
    <div className="space-y-3 rounded-card border border-border bg-paper p-3">
      <div className="flex items-center gap-2 text-sm text-ink">
        <span className="font-medium">{plan.from_email}</span>
        <MoveRight className="h-4 w-4 text-muted" aria-hidden />
        <span className="font-medium">{plan.to_email}</span>
      </div>

      <div className="space-y-1">
        <p className="text-xs font-semibold uppercase tracking-wide text-muted">What moves</p>
        <ul className="space-y-0.5 text-sm text-ink">
          {plan.moves_child ? (
            <li>
              {plan.child_name} — their student record{" "}
              <span className="text-muted">(and so the attendance roll)</span>
            </li>
          ) : null}
          {plan.registrations.map((r) => (
            <li key={r.id}>
              <span className="font-mono text-xs">{r.payment_ref}</span> — {r.child_name}
              {r.program_label ? <span className="text-muted"> · {r.program_label}</span> : null}
            </li>
          ))}
          {plan.invoices_moving.map((n) => (
            <li key={n} className="text-muted">
              Invoice {n}
            </li>
          ))}
        </ul>
      </div>

      <p className="text-xs text-muted">
        {plan.from_email} keeps {countLabel(plan.remaining_children, "student")} and{" "}
        {countLabel(plan.remaining_enrolments, "registration")}.
      </p>

      {plan.warnings.length > 0 ? (
        <ul className="space-y-1.5">
          {plan.warnings.map((w) => (
            <li key={w} className="flex items-start gap-2 text-xs text-ink">
              <AlertTriangle className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-accent-fg" aria-hidden />
              <span>{w}</span>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

/** A request already out and waiting on the parent. */
function PendingPanel({
  move,
  onCancel,
  onResend,
  busy,
}: {
  move: EnrolmentMove;
  onCancel: () => void;
  onResend: () => void;
  busy: boolean;
}) {
  return (
    <div className="space-y-2 rounded-card border border-accent bg-accent/10 p-3">
      <div className="flex items-center gap-2 text-sm font-semibold text-ink">
        <Clock className="h-4 w-4" aria-hidden />
        Waiting on {move.to_email}
      </div>
      <p className="text-xs text-muted">
        Asked {formatWhen(move.requested_at)} · link expires {formatWhen(move.expires_at)}. Nothing
        has moved.
      </p>
      <div className="flex gap-2">
        <Button type="button" size="sm" variant="outline" onClick={onResend} disabled={busy}>
          Send the link again
        </Button>
        <Button type="button" size="sm" variant="outline" onClick={onCancel} disabled={busy}>
          Withdraw the request
        </Button>
      </div>
    </div>
  );
}

function SentPanel({ to, emailed, onDone }: { to: string; emailed: boolean; onDone: () => void }) {
  return (
    <div className="space-y-4">
      <div className="space-y-2 rounded-card border border-border bg-paper p-4">
        <div className="flex items-center gap-2 text-sm font-semibold text-ink">
          <MailCheck className="h-4 w-4" aria-hidden />
          {emailed ? `Sent to ${to}` : `Request saved for ${to}`}
        </div>
        <p className="text-sm text-muted">
          {emailed
            ? "They have a link asking them to confirm the child is theirs. The records move the moment they click it — and not before."
            : "We couldn't get the email out just now. The request is saved, so use “Send the link again” in a moment."}
        </p>
        <p className="text-xs text-muted">
          Nothing has changed on this registration yet. You'll get an email when it goes through.
        </p>
      </div>
      <DialogFooter>
        <Button type="button" onClick={onDone}>
          Done
        </Button>
      </DialogFooter>
    </div>
  );
}

/** A pending move, badged on the row it belongs to. */
export function MovePendingBadge({ move }: { move: EnrolmentMove }) {
  return (
    <Badge variant="muted" title={`Waiting for ${move.to_email} to confirm the move`}>
      Move pending
    </Badge>
  );
}

function countLabel(n: number, noun: string): string {
  return `${n} ${noun}${n === 1 ? "" : "s"}`;
}

function formatWhen(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
}
